/**
 * SpendingAnalytics - Spending by category analytics tab (WP-CAT-12, WP-CAT-13)
 *
 * Features:
 * - Date range selector (presets + custom)
 * - Pie/bar chart showing expense distribution
 * - Table with spending breakdown
 * - Click on category navigates to transactions
 * - Export to CSV
 * - SVG icons via CategoryIcon component
 *
 * API: GET /transactions/spending-by-category
 */
import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import Input from '@/components/ui/Input';
import { CategoryIcon } from '@/components/categories';
import {
  useSpendingByCategory,
  getDateRangeFromPreset,
  type DateRangePreset,
} from '@/hooks/useSpendingByCategory';

// ============================================================================
// Constants
// ============================================================================

const CHART_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Violet
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#F97316', // Orange
  '#6366F1', // Indigo
];

// ============================================================================
// Component
// ============================================================================

export default function SpendingAnalytics() {
  const navigate = useNavigate();

  // State
  const [datePreset, setDatePreset] = useState<DateRangePreset>('last30');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [chartType, setChartType] = useState<'pie' | 'bar'>('pie');

  // Calculate date range
  const dateRange = useMemo(
    () => getDateRangeFromPreset(datePreset, customStartDate, customEndDate),
    [datePreset, customStartDate, customEndDate]
  );

  // Fetch spending data
  const { data, isLoading, isError, refetch } = useSpendingByCategory({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  // Transform data for Recharts and filter/sort
  interface ChartDataItem {
    name: string;
    total: number;
    categoryId: string;
    [key: string]: string | number; // Index signature for Recharts compatibility
  }

  const chartData = useMemo((): ChartDataItem[] => {
    if (!data) return [];
    return [...data]
      .filter((item) => item.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10)
      .map((item) => ({
        name: item.categoryName,
        total: Math.abs(item.total),
        categoryId: item.categoryId,
      }));
  }, [data]);

  // Calculate total spending
  const totalSpending = useMemo(() => {
    return data?.reduce((sum, item) => sum + Math.abs(item.total), 0) || 0;
  }, [data]);

  // Handle category click - navigate to transactions
  const handleCategoryClick = useCallback(
    (categoryId: string) => {
      navigate(
        `/transactions?category_id=${categoryId}&start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`
      );
    },
    [navigate, dateRange]
  );

  // Handle export to CSV
  const handleExport = useCallback(() => {
    if (!data) return;

    const csvHeader = 'Category,Total Amount,Transaction Count,Percentage,Period\n';
    const csvRows = data.map((item) =>
      [
        `"${item.categoryName}"`,
        Math.abs(item.total).toFixed(2),
        item.transactionCount,
        item.percentOfTotal.toFixed(1),
        `${dateRange.startDate} to ${dateRange.endDate}`,
      ].join(',')
    );

    const csvContent = csvHeader + csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `spending-by-category-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [data, dateRange]);

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.abs(value));
  };

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-gray-500">Loading analytics...</p>
        </div>
      </Card>
    );
  }

  // Error state
  if (isError) {
    return (
      <Card>
        <Alert variant="error" className="mb-4">
          Failed to load spending analytics. Please try again.
        </Alert>
        <Button onClick={() => refetch()} variant="primary">
          Retry
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <div className="flex flex-wrap items-end gap-4">
          {/* Date preset */}
          <div>
            <Select
              id="date-preset"
              label="Date Range"
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value as DateRangePreset)}
              options={[
                { value: 'last30', label: 'Last 30 Days' },
                { value: 'last90', label: 'Last 90 Days' },
                { value: 'thisYear', label: 'This Year' },
                { value: 'lastYear', label: 'Last Year' },
                { value: 'custom', label: 'Custom' },
              ]}
            />
          </div>

          {/* Custom date inputs */}
          {datePreset === 'custom' && (
            <>
              <div>
                <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <Input
                  id="start-date"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <Input
                  id="end-date"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
            </>
          )}

          {/* Chart type toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Chart Type
            </label>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                type="button"
                onClick={() => setChartType('pie')}
                className={`px-3 py-2 text-sm ${
                  chartType === 'pie'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Pie
              </button>
              <button
                type="button"
                onClick={() => setChartType('bar')}
                className={`px-3 py-2 text-sm ${
                  chartType === 'bar'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Bar
              </button>
            </div>
          </div>

          {/* Export button */}
          <div className="ml-auto">
            <Button variant="outline" onClick={handleExport} disabled={!data?.length}>
              Export CSV
            </Button>
          </div>
        </div>
      </Card>

      {/* Summary */}
      <Card>
        <div className="text-center py-4">
          <p className="text-sm text-gray-500">Total Spending</p>
          <p className="text-3xl font-bold text-gray-900">{formatCurrency(totalSpending)}</p>
          <p className="text-sm text-gray-500 mt-1">
            {dateRange.startDate} to {dateRange.endDate}
          </p>
        </div>
      </Card>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader title="Spending by Category" />
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'pie' ? (
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="total"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    label={({ name, percent }) =>
                      `${name ?? ''} (${((percent ?? 0) * 100).toFixed(0)}%)`
                    }
                    labelLine={true}
                  >
                    {chartData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                        cursor="pointer"
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatCurrency(Number(value))}
                  />
                  <Legend />
                </PieChart>
              ) : (
                <BarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                  <YAxis type="category" dataKey="name" width={150} />
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  <Bar dataKey="total" fill="#3B82F6">
                    {chartData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                        cursor="pointer"
                      />
                    ))}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardHeader title="Spending Breakdown" />
        {data && data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transactions
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    % of Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((item, index) => (
                  <tr
                    key={item.categoryId || `uncategorized-${index}`}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => item.categoryId && handleCategoryClick(item.categoryId)}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <CategoryIcon
                          name={item.categoryIcon}
                          size="md"
                          className="text-gray-600"
                        />
                        <span className="text-sm font-medium text-gray-900">
                          {item.categoryName || 'Uncategorized'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatCurrency(item.total)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                      {item.transactionCount}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                      {item.percentOfTotal.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No spending data for the selected period.
          </div>
        )}
      </Card>
    </div>
  );
}
